import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { format, parseISO, addMonths } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { CheckCircle, Clock, Loader2, ChevronLeft, Info, BadgeCheck, BadgeX, Mail } from "lucide-react";

const BIKE_CHOICES = ["Trishaw Only", "Wheelchair Bike Only", "Trishaw and Wheelchair Bike"];

export default function EmbedPage() {
  const [config, setConfig]               = useState({ intro_text: "", primary_color: "#15803d", accent_color: "#ea580c" });
  const [slots, setSlots]                 = useState([]);
  const [loading, setLoading]             = useState(true);
  const [maintenance, setMaintenance]     = useState(false);
  const [selectedDate, setSelectedDate]   = useState(null);
  const [selectedSlot, setSelectedSlot]   = useState(null);
  const [step, setStep]                   = useState(1);
  const [form, setForm]                   = useState({ name: "", email: "", phone: "", meeting_point: "", time_flexibility: "", extra_info: "", trishaw_consent: false, request_consent: false });
  const [bookingCode, setBookingCode]     = useState("");
  const [codeStatus, setCodeStatus]       = useState(null);
  const [codeChecking, setCodeChecking]   = useState(false);
  const [bikeChoice, setBikeChoice]       = useState("Trishaw Only");
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [portalUrl, setPortalUrl] = useState("");
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const debounceRef = useRef(null);

  const isGroup = codeStatus === "valid";

  const primary = config.primary_color || "#15803d";
  const accent  = config.accent_color  || "#ea580c";
  const primaryBg     = `${primary}18`;
  const primaryBorder = `${primary}35`;

  const renderTurnstile = useCallback(() => {
    if (!turnstileRef.current || !window.turnstile) return;
    if (turnstileWidgetId.current !== null) {
      try { window.turnstile.remove(turnstileWidgetId.current); } catch (e) { console.debug("turnstile remove (embed render):", e?.message); }
      turnstileWidgetId.current = null;
    }
    setTurnstileToken("");
    turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
      sitekey: "0x4AAAAAAC86gAJOKBga3OaP",
      callback: (token) => setTurnstileToken(token),
      "expired-callback": () => setTurnstileToken(""),
      "error-callback": () => setTurnstileToken(""),
      theme: "light",
    });
  }, []);

  const fetchSlots = () => api.getAvailableSlots().then((r) => setSlots(r.data.slots || []));

  useEffect(() => {
    Promise.all([
      fetchSlots(),
      api.getEmbedConfig().then((r) => setConfig(r.data)),
      api.getMaintenanceStatus().then((r) => { if (r.data.maintenance_mode) setMaintenance(true); }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Render Turnstile when entering step 2
  useEffect(() => {
    if (step !== 2) return;
    const timer = setTimeout(renderTurnstile, 200);
    return () => clearTimeout(timer);
  }, [step, renderTurnstile]);

  // Debounced booking code validation
  useEffect(() => {
    if (!bookingCode.trim()) { setCodeStatus(null); return; }
    clearTimeout(debounceRef.current);
    setCodeChecking(true);
    debounceRef.current = setTimeout(() => {
      api.validateBookingCode(bookingCode.trim())
        .then((r) => setCodeStatus(r.data.valid ? "valid" : "invalid"))
        .catch(() => setCodeStatus("invalid"))
        .finally(() => setCodeChecking(false));
    }, 500);
  }, [bookingCode]);

  const availableDateSet = useMemo(() => new Set(slots.map((s) => s.date)), [slots]);
  const slotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return slots.filter((s) => s.date === format(selectedDate, "yyyy-MM-dd"));
  }, [slots, selectedDate]);
  const firstAvailableDate = useMemo(() => (slots.length ? parseISO(slots[0].date) : undefined), [slots]);
  const isDateDisabled = (date) => !availableDateSet.has(format(date, "yyyy-MM-dd"));

  const handleDateSelect = (date) => { setSelectedDate(date); setSelectedSlot(null); };
  const handleSelectSlot = (slot)  => { setSelectedSlot(slot); setStep(2); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the human verification challenge.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.createBooking({
        requester_name: form.name,
        requester_email: form.email,
        requester_phone: form.phone,
        booking_datetime_iso: selectedSlot.datetime_utc,
        meeting_point: isGroup ? "Coffee@The Edge" : form.meeting_point,
        time_flexibility: isGroup ? "" : form.time_flexibility,
        trishaw_consent: isGroup ? true : form.trishaw_consent,
        request_consent: form.request_consent,
        booking_code: isGroup ? bookingCode.trim() : null,
        bike_choice: bikeChoice,
        extra_info: form.extra_info,
        turnstile_token: turnstileToken,
      });
      if (res.data?.portal_url) setPortalUrl(res.data.portal_url);
      await fetchSlots();
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit booking. Please try again.");
      if (turnstileWidgetId.current !== null && window.turnstile) {
        try { window.turnstile.reset(turnstileWidgetId.current); } catch (e) { console.debug("turnstile reset (embed submit):", e?.message); }
      }
      setTurnstileToken("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(1); setSelectedDate(null); setSelectedSlot(null);
    setForm({ name: "", email: "", phone: "", meeting_point: "", time_flexibility: "", extra_info: "", trishaw_consent: false, request_consent: false });
    setBookingCode(""); setCodeStatus(null); setBikeChoice(""); setTurnstileToken(""); setError(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: primary }} />
    </div>
  );

  if (maintenance) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: "#fef3c7" }}>
          <Clock className="w-7 h-7 text-amber-600" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Temporarily Unavailable</h2>
        <p className="text-slate-500 text-sm">Booking is temporarily unavailable for maintenance. Please check back shortly.</p>
      </div>
    </div>
  );

  if (step === 3) return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: primaryBg }}>
          <CheckCircle className="w-7 h-7" style={{ color: primary }} />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Request Submitted!</h2>
        <p className="text-slate-500 text-sm">Thank you! You will receive a confirmation email once a volunteer confirms your booking.</p>

        {/* Email notice */}
        <div className="mt-5 bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 text-left" data-testid="embed-email-notice">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-amber-200 rounded-full flex items-center justify-center shrink-0">
              <Mail className="w-4 h-4 text-amber-700" />
            </div>
            <p className="font-bold text-amber-900 text-sm">Check your email</p>
          </div>
          <p className="text-amber-800 text-xs leading-relaxed mb-1.5">All booking notifications will be sent from:</p>
          <p className="font-bold text-amber-900 text-sm bg-amber-100 rounded-lg px-3 py-1.5 text-center mb-2">bookings@doddervalleycycling.com</p>
          <p className="text-amber-700 text-xs leading-relaxed">
            <strong>Can't see an email?</strong> Please check your <strong>Spam</strong> or <strong>Junk</strong> folder.
          </p>
        </div>

        {portalUrl && (
          <a href={portalUrl} target="_top" className="mt-4 inline-block font-semibold text-sm hover:underline" style={{ color: primary }} data-testid="embed-view-bookings-link">
            View My Bookings
          </a>
        )}

        <button
          onClick={() => { handleReset(); setPortalUrl(""); }}
          className="mt-3 font-semibold hover:underline text-sm block"
          style={{ color: primary }}
          data-testid="embed-make-another"
        >
          Make another booking
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "'Public Sans', sans-serif" }}>
      <div className="max-w-3xl mx-auto px-4 py-6">

        {/* Configurable intro text */}
        {config.intro_text && (
          <div
            className="mb-5 flex gap-2 p-4 rounded-xl"
            style={{ backgroundColor: primaryBg, borderColor: primaryBorder, border: "1px solid" }}
            data-testid="embed-intro"
          >
            <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: primary }} />
            <p className="text-slate-600 text-sm leading-relaxed">{config.intro_text}</p>
          </div>
        )}

        {/* Sponsor message banner */}
        {config.sponsor_message_enabled && config.sponsor_message && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5 text-amber-800 text-sm text-center" data-testid="embed-sponsor-banner">
            {config.sponsor_url ? (
              <a href={config.sponsor_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-900 transition-colors">{config.sponsor_message}</a>
            ) : (
              config.sponsor_message
            )}
          </div>
        )}

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="embed-calendar-step">
            {/* Calendar */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Choose a Date</p>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                defaultMonth={firstAvailableDate}
                fromDate={new Date()}
                toDate={addMonths(new Date(), 3)}
                disabled={isDateDisabled}
                className="rounded-xl w-full"
              />
            </div>

            {/* Time slots */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                {selectedDate ? `Available Times — ${format(selectedDate, "EEE d MMM")}` : "Choose a Time"}
              </p>

              {!selectedDate ? (
                <div className="flex-1 flex flex-col items-center justify-center py-10 text-center">
                  <Clock className="w-9 h-9 text-slate-200 mb-3" />
                  <p className="text-slate-400 text-sm">Select a date to see available slots</p>
                </div>
              ) : slotsForDate.length === 0 ? (
                <div className="flex-1 flex items-center justify-center py-10 text-center">
                  <p className="text-slate-400 text-sm">No slots available on this date.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {slotsForDate.map((slot) => (
                    <button
                      key={slot.datetime_utc}
                      onClick={() => handleSelectSlot(slot)}
                      data-testid={`embed-slot-${slot.time}`}
                      className="py-3 px-3 rounded-xl border-2 font-semibold text-sm transition-all hover:opacity-90"
                      style={{ borderColor: primaryBorder, color: primary, backgroundColor: "white" }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primaryBg; e.currentTarget.style.borderColor = primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "white"; e.currentTarget.style.borderColor = primaryBorder; }}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && selectedSlot && (
          <div className="max-w-sm mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="embed-form-step">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-1 text-slate-400 hover:text-slate-600 text-sm mb-4 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>

            {/* Selected slot summary */}
            <div className="rounded-xl p-3 mb-5 text-sm" style={{ backgroundColor: primaryBg }}>
              <p className="font-semibold" style={{ color: primary }}>{format(selectedDate, "EEEE, d MMMM yyyy")}</p>
              <p className="text-sm" style={{ color: `${primary}cc` }}>at {selectedSlot.time}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Full Name</Label>
                <Input required data-testid="embed-name" value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name" className="h-11" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Email Address</Label>
                <Input type="email" required data-testid="embed-email" value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="your@email.com" className="h-11" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Phone Number</Label>
                <Input required data-testid="embed-phone" value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="+353 87 000 0000" className="h-11" />
              </div>

              {/* Booking Code */}
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Booking Code</Label>
                <div className="relative">
                  <Input data-testid="embed-booking-code" value={bookingCode}
                    onChange={(e) => setBookingCode(e.target.value)}
                    placeholder="If you have a booking code, enter it here, otherwise leave blank."
                    className="h-11 pr-10" />
                  {codeChecking && <Loader2 className="absolute right-3 top-3 w-5 h-5 animate-spin text-slate-400" />}
                  {!codeChecking && codeStatus === "valid" && <BadgeCheck className="absolute right-3 top-3 w-5 h-5 text-green-600" />}
                  {!codeChecking && codeStatus === "invalid" && <BadgeX className="absolute right-3 top-3 w-5 h-5 text-red-500" />}
                </div>
                {codeStatus === "valid" && <p className="text-green-700 text-xs mt-1 font-medium">Group booking code accepted.</p>}
                {codeStatus === "invalid" && <p className="text-red-600 text-xs mt-1">Unrecognised booking code. Please check and try again, or leave blank.</p>}
              </div>

              {/* Group booking fields */}
              {isGroup ? (
                <>
                  <div className="rounded-xl p-3 text-sm" style={{ backgroundColor: primaryBg }}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-0.5" style={{ color: primary }}>Meeting Point</p>
                    <p className="font-semibold text-slate-800">Coffee@The Edge</p>
                    <p className="text-xs text-slate-500 mt-0.5">Fixed for group bookings</p>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Bike Choice <span className="text-red-500">*</span></Label>
                    <select required data-testid="embed-bike-choice"
                      value={bikeChoice} onChange={(e) => setBikeChoice(e.target.value)}
                      className="w-full h-11 bg-slate-50 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:border-green-600">
                      <option value="">Select bike(s) required...</option>
                      {BIKE_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {/* Meeting Point */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Meeting Point <span className="text-red-500">*</span></Label>
                    <select
                      required data-testid="embed-meeting-point"
                      value={form.meeting_point}
                      onChange={(e) => setForm((p) => ({ ...p, meeting_point: e.target.value }))}
                      className="w-full h-11 bg-slate-50 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:border-green-600"
                    >
                      <option value="">Select a meeting point...</option>
                      <option value="Meeting Point A - The Weir">Meeting Point A — The Weir</option>
                      <option value="Meeting Point B - Opp. Speaker Connolly">Meeting Point B — Opp. Speaker Connolly</option>
                    </select>
                  </div>

                  {/* Bike Type (single booking) */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5 block">Bike Type <span className="text-red-500">*</span></Label>
                    <select required data-testid="embed-bike-type"
                      value={bikeChoice} onChange={(e) => setBikeChoice(e.target.value)}
                      className="w-full h-11 bg-slate-50 border border-slate-200 rounded-md px-3 text-sm focus:outline-none focus:border-green-600">
                      <option value="Trishaw Only">Trishaw</option>
                      <option value="Wheelchair Bike Only">Wheelchair Bike</option>
                    </select>
                  </div>

                  {/* Time Flexibility */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Time Flexibility <span className="text-red-500">*</span></Label>
                    <p className="text-xs text-slate-400 mb-1.5">Any flexibility on the day? State times or type "no".</p>
                    <textarea required data-testid="embed-time-flexibility"
                      value={form.time_flexibility}
                      onChange={(e) => setForm((p) => ({ ...p, time_flexibility: e.target.value }))}
                      rows={2} placeholder='"A start time between 10am and 1pm" or "no"'
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-600 resize-none" />
                  </div>

                  {/* Trishaw consent */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input type="checkbox" required data-testid="embed-trishaw-consent"
                        checked={form.trishaw_consent}
                        onChange={(e) => setForm((p) => ({ ...p, trishaw_consent: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 accent-green-700 shrink-0" />
                      <span className="text-slate-700 text-xs leading-relaxed">
                        <strong>I understand</strong> the service is only for those who can't ride bikes because of a particular medical, cognitive or physical challenge. <span className="text-red-500">*</span>
                      </span>
                    </label>
                  </div>
                </>
              )}

              {/* Request consent — always shown */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" required data-testid="embed-request-consent"
                    checked={form.request_consent}
                    onChange={(e) => setForm((p) => ({ ...p, request_consent: e.target.checked }))}
                    className="mt-0.5 w-4 h-4 accent-green-700 shrink-0" />
                  <span className="text-slate-700 text-xs leading-relaxed">
                    <strong>I understand</strong> this is a Booking <em>request</em>, not a confirmed booking until a follow up confirmation email. <span className="text-red-500">*</span>
                  </span>
                </label>
              </div>

              {/* Extra info */}
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Extra Information</Label>
                <p className="text-xs text-slate-400 mb-1.5">If not, leave blank.</p>
                <textarea data-testid="embed-extra-info"
                  value={form.extra_info}
                  onChange={(e) => setForm((p) => ({ ...p, extra_info: e.target.value }))}
                  rows={2} placeholder="Any additional details..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-600 resize-none" />
              </div>

              {/* Cloudflare Turnstile */}
              <div className="flex flex-col items-center" data-testid="embed-turnstile-container">
                <div ref={turnstileRef}></div>
                {!turnstileToken && (
                  <p className="text-slate-400 text-xs mt-1">Please verify you are human to continue.</p>
                )}
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              <button
                type="submit"
                disabled={submitting || !turnstileToken}
                data-testid="embed-submit"
                className="w-full h-11 text-white rounded-full font-semibold flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                style={{ backgroundColor: accent }}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Submitting..." : "Request Booking"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
